import { supabase } from "@/integrations/supabase/client";
import {
  OnboardingFlow,
  OnboardingFlowWithPages,
  OnboardingFlowFormData,
  OnboardingPage,
  OnboardingPageFormData,
} from "@/types/onboarding";

const transformFlow = (row: any): OnboardingFlow => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  description: row.description,
  isActive: row.is_active,
  isSkippable: row.is_skippable,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  pageCount: row.page_count,
});

// Parse JSONB that may come back as a string due to `as any` table casting
const parseJsonb = (value: any): Record<string, string> | null => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) return parsed;
    } catch {
      // plain string fallback — wrap as English
      return { en: value };
    }
  }
  return null;
};

const transformPage = (row: any): OnboardingPage => ({
  id: row.id,
  flowId: row.flow_id,
  position: row.position,
  mediaType: row.media_type,
  mediaUrl: row.media_url,
  mediaBgColor: row.media_bg_color,
  tag: parseJsonb(row.tag),
  tagStyle: row.tag_style,
  title: parseJsonb(row.title) || { en: "" },
  content: parseJsonb(row.content),
  buttonText: parseJsonb(row.button_text) || { en: "Next" },
  isActive: row.is_active,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const fetchOnboardingFlows = async (): Promise<OnboardingFlow[]> => {
  const { data, error } = await supabase
    .from("onboarding_flows" as any)
    .select("*, onboarding_pages(count)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map((row: any) => ({
    ...transformFlow(row),
    pageCount: row.onboarding_pages?.[0]?.count ?? 0,
  }));
};

export const fetchOnboardingFlowById = async (
  id: number
): Promise<OnboardingFlowWithPages | null> => {
  const { data, error } = await supabase
    .from("onboarding_flows" as any)
    .select("*")
    .eq("id", id)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  if (!data) return null;

  const { data: pages, error: pagesError } = await supabase
    .from("onboarding_pages" as any)
    .select("*")
    .eq("flow_id", id)
    .order("position", { ascending: true });
  if (pagesError) throw pagesError;

  return {
    ...transformFlow(data),
    pages: (pages || []).map(transformPage),
  };
};

export const saveOnboardingFlow = async (
  formData: OnboardingFlowFormData,
  id?: number
): Promise<OnboardingFlow> => {
  const payload: Record<string, unknown> = {
    name: formData.name,
    slug: formData.slug,
    description: formData.description || null,
    is_active: formData.isActive,
    is_skippable: formData.isSkippable,
  };

  if (id) {
    const { data, error } = await supabase
      .from("onboarding_flows" as any)
      .update(payload)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    if (!data) throw new Error("No data returned after update");
    return transformFlow(data);
  } else {
    const { data, error } = await supabase
      .from("onboarding_flows" as any)
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    if (!data) throw new Error("No data returned after insert");
    return transformFlow(data);
  }
};

export const deleteOnboardingFlow = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from("onboarding_flows" as any)
    .delete()
    .eq("id", id);
  if (error) throw error;
};

export const saveOnboardingPage = async (
  formData: OnboardingPageFormData,
  id?: number
): Promise<OnboardingPage> => {
  const payload: Record<string, unknown> = {
    flow_id: formData.flowId,
    position: formData.position,
    media_type: formData.mediaType || null,
    media_url: formData.mediaUrl || null,
    media_bg_color: formData.mediaBgColor || null,
    tag: formData.tag && Object.values(formData.tag).some(v => v) ? formData.tag : null,
    tag_style: formData.tagStyle || null,
    title: formData.title,
    content: formData.content && Object.values(formData.content).some(v => v) ? formData.content : null,
    button_text: formData.buttonText,
    is_active: formData.isActive,
  };

  if (id) {
    const { data, error } = await supabase
      .from("onboarding_pages" as any)
      .update(payload)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    if (!data) throw new Error("No data returned after update");
    return transformPage(data);
  } else {
    const { data, error } = await supabase
      .from("onboarding_pages" as any)
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    if (!data) throw new Error("No data returned after insert");
    return transformPage(data);
  }
};

export const deleteOnboardingPage = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from("onboarding_pages" as any)
    .delete()
    .eq("id", id);
  if (error) throw error;
};

export const updateOnboardingPagePositions = async (
  pages: { id: number; position: number }[]
): Promise<void> => {
  for (const page of pages) {
    const { error } = await supabase
      .from("onboarding_pages" as any)
      .update({ position: page.position })
      .eq("id", page.id);
    if (error) throw error;
  }
};
